import Vue from "vue";
import constant from "../../constant";
import router from "../../router";
import FileDownloadService from "../../services/FileDownloadService";

const api = constant.api + "consignments";

// ─── State ───────────────────────────────────────────────────────────────────
const state = () => ({
  all: [],
  consignments: [],
  consignment: {},
  costs: [],
  allCosts: [],
  items: [],
  allItems: [],
});

// ─── Getters ─────────────────────────────────────────────────────────────────
const getters = {
  getAll:         (state) => state.all,
  getConsignment: (state) => state.consignment,
  getConsignments:(state) => state.consignments,
  getCosts:       (state) => state.costs,
  getAllCosts:     (state) => state.allCosts,
  getItems:       (state) => state.items,
  getAllItems:     (state) => state.allItems,
};

// ─── Actions ─────────────────────────────────────────────────────────────────
const actions = {

  // ── Consignments ────────────────────────────────────────────────────────────

  async getAll({ commit }) {
    const data = await this.$api.fetch(api);
    commit("setConsignments", data);
  },

  async show({ commit }, reference) {
    const response = await this.$api.show(api + "/" + reference);
    commit("setConsignment", response.data);
  },

  async store({ }, data) {
    const response = await this.$api.create(api, data, { confirm: false });
    if (response && response.data && response.data.status === "success") {
      return response.data.data || response.data;
    }
    return null;
  },

  async update({ }, data) {
    const response = await this.$api.update(api + "/" + data.reference, data, { confirm: false });
    if (response && response.data && response.data.status === "success") {
      router.push({ name: "consignments-index" });
    }
  },

  async destroy({ }, reference) {
    // $api.delete() shows confirmation dialog by default
    const response = await this.$api.delete(api + "/" + reference);
    if (response && response.data && response.data.status === "success") {
      router.push({ name: "consignments-index" });
    }
  },

  async filter({ commit }, data) {
    const params = {};
    if (data.society_reference)  params.society_reference  = data.society_reference;
    if (data.startDate)          params.startDate           = data.startDate;
    if (data.endDate)            params.endDate             = data.endDate;
    if (data.user_reference)     params.user_reference      = data.user_reference;
    if (data.item_reference)     params.item_reference      = data.item_reference;
    if (data.cost_reference)     params.cost_reference      = data.cost_reference;
    if (data.supplier_reference) params.supplier_reference  = data.supplier_reference;
    if (data.invoice_number && String(data.invoice_number).trim()) {
      params.invoice_number = String(data.invoice_number).trim();
    }
    const consignments = await this.$api.fetch(api, params);
    commit("setConsignments", consignments);
  },

  // ── Consignment Costs ────────────────────────────────────────────────────────

  async getAllCosts({ commit }) {
    const data = await this.$api.fetch(api + "_costs");
    commit("setAllCosts", data);
  },

  async getCosts({ commit }) {
    const data = await this.$api.fetch(api + "_costs");
    commit("setCosts", data);
  },

  async storeCost({ commit }, data) {
    const response = await this.$api.create(api + "_costs", data, {
      confirm: false,
    });
    if (!response || !response.data) {
      throw new Error("Réponse API invalide (consignments_costs).");
    }
    if (response.data.status !== "success") {
      throw new Error(
        response.data.message ||
          "Impossible d’ajouter la facture au dossier (consignments_costs)."
      );
    }
    if (!response.data.data) {
      throw new Error("Réponse sans liste consignments_costs.");
    }
    commit("setAllCosts", response.data.data);
  },

  async updateCost({ commit }, data) {
    const response = await this.$api.update(api + "_costs/" + data.reference, data, { confirm: false });
    if (response && response.data && response.data.status === "success" && response.data.data) {
      commit("setAllCosts", response.data.data);
    }
  },

  async destroyCost({ commit }, reference) {
    // $api.delete() shows confirmation dialog by default
    const response = await this.$api.delete(api + "_costs/" + reference);
    if (response && response.data && response.data.status === "success" && response.data.data) {
      commit("setAllCosts", response.data.data);
    }
  },

  // ── Consignment Items ────────────────────────────────────────────────────────

  async getAllItems({ commit }) {
    const data = await this.$api.fetch(api + "_items");
    commit("setAllItems", data);
  },

  async getItems({ commit }) {
    const data = await this.$api.fetch(api + "_items");
    commit("setItems", data);
  },

  async storeItem({ commit }, data) {
    const response = await this.$api.create(api + "_items", data, {
      confirm: false,
    });
    if (!response || !response.data) {
      throw new Error("Réponse API invalide (consignments_items).");
    }
    if (response.data.status !== "success") {
      throw new Error(
        response.data.message ||
          "Impossible d’ajouter la ligne au dossier (consignments_items)."
      );
    }
    if (!response.data.data) {
      throw new Error("Réponse sans liste consignments_items.");
    }
    commit("setAllItems", response.data.data);
  },

  async updateItem({ commit }, data) {
    const id = data.id;
    const body =
      data.formData instanceof FormData ? data.formData : (() => {
        const { id: _omit, ...rest } = data;
        return rest;
      })();
    const response = await this.$api.update(
      api + "_items/" + id,
      body,
      { confirm: false }
    );
    if (response && response.data && response.data.status === "success" && response.data.data) {
      commit("setAllItems", response.data.data);
    }
  },

  async destroyItem({ commit }, id) {
    // $api.delete() shows confirmation dialog by default
    const response = await this.$api.delete(api + "_items/" + id);
    if (response && response.data && response.data.status === "success" && response.data.data) {
      commit("setAllItems", response.data.data);
    }
  },

  // ── Queue to Purchase ────────────────────────────────────────────────────────

  async queuePurchaseItems({ }, reference) {
    const response = await this.$api.create(
      api + `/${reference}/queue-purchase-items`,
      {},
      { confirm: false }
    );
    if (response && response.data && response.data.status === "success") {
      return response.data.data || true;
    }
    return null;
  },

  // ── Invoice file download ────────────────────────────────────────────────────

  async downloadInvoiceFile({ }, reference) {
    const url = constant.api + `consignments_costs/${reference}/download-invoice`;
    const response = await this.$api.instance.get(url, { responseType: "blob" });
    const ct = (response.headers && response.headers["content-type"]) || "application/pdf";
    const blob = new Blob([response.data], { type: ct });
    FileDownloadService.downloadFile(blob, `facture_${reference}.pdf`);
  },

  async exportDetailsExcel({ dispatch }, reference) {
    if (!reference) {
      return;
    }
    const loading = Vue.prototype.$loading;
    if (loading && loading.start) {
      loading.start();
    }
    try {
      const url =
        constant.api +
        `consignments/${encodeURIComponent(String(reference))}/export_excel`;
      const response = await this.$api.instance.get(url, {
        responseType: "blob",
        headers: {
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      });
      const blob = new Blob([response.data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const safeRef = String(reference).replace(/[^\w.-]+/g, "_");
      const dateStr = new Date().toISOString().slice(0, 10);
      FileDownloadService.downloadFile(
        blob,
        `dossier_import_${safeRef}_${dateStr}.xlsx`
      );
      dispatch(
        "notification/store",
        { status: "success", message: "Export terminé avec succès !" },
        { root: true }
      );
    } catch (e) {
      console.error(e);
      dispatch(
        "notification/store",
        {
          status: "error",
          message: "Erreur lors de l'export Excel.",
        },
        { root: true }
      );
    } finally {
      if (loading && loading.stop) {
        loading.stop();
      }
    }
  },
};

// ─── Mutations ────────────────────────────────────────────────────────────────
const mutations = {
  setConsignments(state, consignments) {
    state.all = consignments;
    state.consignments = consignments;
  },
  setConsignment(state, consignment) {
    state.consignment = consignment;
  },
  setCosts(state, costs) {
    state.costs = costs;
  },
  setAllCosts(state, costs) {
    state.allCosts = costs;
    state.costs = costs;
  },
  setItems(state, items) {
    state.items = items;
  },
  setAllItems(state, items) {
    state.allItems = items;
    state.items = items;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
